
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { ValidationProvider } from 'vee-validate';
import IconButton from '@/components/shared/suites/IconButton.vue';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import RangeSlider from '@/components/shared/RangeSlider.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import TextField from '@/components/shared/TextField.vue';
import '@/validation-rules';

@Component<FeeRanges>({
	components: {
		IconButton,
		ValidationProvider,
		InfoBanner,
		RangeSlider,
		TextButton,
		TextField
	}
})
export default class FeeRanges extends Vue {
	@Prop({ type: Array, default: () => [] }) private ranges!: DeliveryFeeRange[];
	@Prop({ type: Number, required: true }) private maxRadius!: number;
	@Prop({ type: Boolean, default: true }) private chargeTypeIsDollar!: boolean;
	@Getter('getCurrencySymbol', { namespace: 'auth' }) private currencySymbol!: string;
	@Watch('ranges')
	private onRangesChanged(): void {
		this.resetSliderRange();
	}
	/**
	 * Reset slider range when max radius changes to ensure slider thumbs are within bounds
	 * We also trim any existing ranges that exceed the new max radius, emitting the updated ranges if any were trimmed
	 * or completely removed.
	 *
	 * @return {void}
	 */
	@Watch('maxRadius')
	private onMaxRadiusChanged(): void {
		if (this.ranges.length && this.maxRadius) {
			const trimmed = this.ranges
				.filter(range => range.from < this.maxRadius)
				.map((range, i, arr) => {
					if (i === arr.length - 1 && range.to > this.maxRadius) {
						return { ...range, to: this.maxRadius };
					}
					return range;
				});

			if (trimmed.length !== this.ranges.length || trimmed.some((r, i) => r.to !== this.ranges[i].to)) {
				this.$emit('input', trimmed);
			}
		}
		this.sliderRange = [this.nextRangeStart, this.sliderMax];
	}

	private sliderRange: number[] = [0, 5];

	private get isRangeFullyCovered(): boolean {
		return this.nextRangeStart >= this.maxRadius;
	}

	private get isAddDisabled(): boolean {
		return this.ranges.length >= 8 || this.isRangeFullyCovered;
	}

	private get feeValidationRules(): string {
		return this.chargeTypeIsDollar ? 'required' : 'required|max_value:100|integer';
	}

	/**
	 * The start of the next range is the end of the last existing range, or 0
	 *
	 * @return {number}
	 */
	private get nextRangeStart(): number {
		if (!this.ranges.length) {
			return 0;
		}
		return this.ranges[this.ranges.length - 1].to;
	}

	/**
	 * Slider max, guaranteeing max > min so Vuetify renders the track on initial mount
	 *
	 * @return {number}
	 */
	private get sliderMax(): number {
		return Math.max(this.maxRadius, this.nextRangeStart + 1);
	}

	private mounted(): void {
		this.resetSliderRange();
	}

	private resetSliderRange(): void {
		const max = this.sliderMax;
		const start = this.nextRangeStart;
		this.sliderRange = [start, max];
	}

	/**
	 * Add a new range with from = sliderRange[0], to = sliderRange[1]
	 * Validates that all existing ranges have a fee before allowing a new one
	 *
	 * @return {Promise<void>}
	 */
	private async addRange(): Promise<void> {
		if (this.ranges.length >= 8 || this.sliderRange[0] === this.sliderRange[1]) {
			return;
		}

		// Validate all existing fee inputs before adding a new range
		const validators = this.$refs.feeValidators as unknown as { validate: () => Promise<{ valid: boolean }> }[];
		if (validators?.length) {
			const results = await Promise.all(validators.map(v => v.validate()));
			if (results.some(r => !r.valid)) {
				return;
			}
		}

		const updatedRanges = [
			...this.ranges,
			{ from: this.sliderRange[0], to: this.sliderRange[1], fee: '' }
		];
		this.$emit('input', updatedRanges);
	}

	/**
	 * Remove the last range
	 *
	 * @return {void}
	 */
	private removeLastRange(): void {
		if (!this.ranges.length) {
			return;
		}
		const updatedRanges = this.ranges.slice(0, -1);
		this.$emit('input', updatedRanges);
	}

	/**
	 * Get the numeric value from a fee string (strip the $ or % suffix)
	 *
	 * @param {string} fee
	 * @return {string}
	 */
	private getFeeValue(fee: string): string {
		if (!fee) {
			return '';
		}
		if (fee.endsWith('$') || fee.endsWith('%')) {
			return fee.slice(0, -1);
		}
		return fee;
	}

	/**
	 * Update a range's fee value, appending the correct suffix
	 *
	 * @param {number} index
	 * @param {string} value
	 * @return {void}
	 */
	private updateRangeFee(index: number, value: string): void {
		const suffix = this.chargeTypeIsDollar ? '$' : '%';
		const updatedRanges = this.ranges.map((range, i) => {
			if (i === index) {
				return { ...range, fee: value ? `${value}${suffix}` : '' };
			}
			return range;
		});
		this.$emit('input', updatedRanges);
	}
}
